// ── Player Controller ──
// Quake/Source-style acceleration + friction movement model.
// Based on CS:GO sv_accelerate / sv_friction / sv_stopspeed values,
// scaled to our coordinate system where 1 unit = 1 meter.
//
// References:
//   - Quake III bg_pmove.c  (id Software)
//   - https://adrianb.io/2015/02/14/bunnyhop.html
//   - CS:GO sv_accelerate=5.6, sv_friction=4.8, sv_stopspeed=75 HU/s
//
// MUST match server movement.go exactly to avoid prediction mismatch.

import {
  GRAVITY,
  JUMP_VELOCITY,
  CROUCH_SPEED_MULT,
  PLAYER_HEIGHT,
  PLAYER_CROUCH_HEIGHT,
  WEAPONS,
  TAG_DURATION,
  TAG_SLOW_AMOUNTS,
  BHOP_SPEED_BONUS,
  BHOP_MAX_MULTIPLIER,
  BHOP_LANDING_WINDOW,
  BHOP_DECAY_RATE,
  // G-M-001: slide constants are owned by shared/constants.json (mirrored
  // into core/config.ts as m/s). Importing here removes the hand-typed
  // duplicates that previously lived in this module and would silently
  // drift when the JSON changes.
  SLIDE_SPEED,
  SLIDE_FRICTION,
  SLIDE_DURATION,
  SLIDE_COOLDOWN,
  SLIDE_MIN_SPEED,
  SLIDE_HEIGHT,
} from '../core/config';
import type { InputState } from '../core/input';
import type { LocalPlayer } from './player';
import { resolveCollisions } from './collision';

// ── Movement constants (CS:GO-scaled to meters) ──
// CS:GO: 1 HU = 0.0254m.  sv_accelerate=5.6 in HU means the same factor in our m/s system
// because it's a multiplier, not a speed.

/** Ground acceleration factor (Source: sv_accelerate = 5.6). */
const GROUND_ACCEL = 5.6;

/** Ground friction factor (Source: sv_friction = 4.8). */
const FRICTION = 4.8;

/**
 * Speed below which friction snaps to zero instead of asymptotically approaching it.
 * CS:GO sv_stopspeed = 75 HU/s = 1.905 m/s.
 */
const STOP_SPEED = 1.9;

/** Air acceleration factor (Source: sv_airaccelerate = 12). */
const AIR_ACCEL = 12;

/**
 * Maximum speed you can add via air strafing.
 * CS:GO air speed cap = 30 HU/s = 0.762 m/s.
 */
const AIR_SPEED_CAP = 0.76;

/** Player collision radius (meters). */
const PLAYER_RADIUS = 0.4;

/**
 * Maximum distance the player can move per collision sub-step (meters).
 * Must be less than the thinnest expanded collider width to prevent tunneling.
 * Thinnest wall (~0.1m) + Minkowski expansion (2 × 0.4m) = 0.9m → use 0.3m.
 */
const MAX_MOVE_STEP = 0.3;

/** Threshold for "is this player meaningfully moving?" */
const MOVING_THRESHOLD = 0.3; // m/s

// ── Slide constants ──
// G-M-001: imported from ../core/config (which mirrors shared/constants.json).
// The previous hand-typed copies of SLIDE_SPEED / SLIDE_FRICTION /
// SLIDE_DURATION / SLIDE_COOLDOWN / SLIDE_MIN_SPEED / SLIDE_HEIGHT have been
// removed so the shared JSON is the single source of truth.

// ── Eye height constants (must match camera.ts) ──
const EYE_HEIGHT_STAND = 1.6;
const EYE_HEIGHT_CROUCH = PLAYER_CROUCH_HEIGHT * 0.9; // ~1.215
const EYE_HEIGHT_SLIDE = SLIDE_HEIGHT * 0.9;           // ~0.81

// ── Public API ──

/**
 * Apply one tick of input to a LocalPlayer.
 * Uses Quake/Source acceleration model for smooth, CS-like movement.
 */
export function applyInput(
  player: LocalPlayer,
  input: InputState,
  dt: number,
): void {
  // ── Tick down tagging timer (must match server UpdateTag) ──
  if (player.tagTimer > 0) {
    player.tagTimer -= dt;
    if (player.tagTimer <= 0) {
      player.tagTimer = 0;
      player.tagSlowdown = 0;
    }
  }

  // ── Tick down slide cooldown ──
  if (player.slideCooldownTimer > 0) {
    player.slideCooldownTimer = Math.max(0, player.slideCooldownTimer - dt);
  }

  // ── Tick down bhop landing window ──
  if (player.bhopLandingTimer > 0) {
    player.bhopLandingTimer = Math.max(0, player.bhopLandingTimer - dt);
  }

  // ── Ground detection (from previous tick's collision resolution) ──
  const onGround = player.isGrounded;

  // ── Bhop landing detection ──
  // If the player just landed from a bhop jump, start the chain window
  if (onGround && player.bhopFromSlide) {
    player.bhopFromSlide = false;
    player.bhopLandingTimer = BHOP_LANDING_WINDOW;
  }

  // ── Slide initiation check ──
  // Trigger: on ground, pressing crouch, not already crouching or sliding,
  // moving fast enough, and cooldown expired (or in bhop chain).
  const hSpeedSq = player.velocity.x * player.velocity.x +
                   player.velocity.z * player.velocity.z;
  const hSpeed = Math.sqrt(hSpeedSq);

  // Bhop chain: skip cooldown if within the landing window
  const inBhopWindow = player.bhopLandingTimer > 0;
  const cooldownOk = player.slideCooldownTimer <= 0 || inBhopWindow;

  if (
    input.crouch &&
    !player.isCrouching &&
    !player.isSliding &&
    onGround &&
    hSpeed > SLIDE_MIN_SPEED &&
    cooldownOk &&
    player.tagTimer <= 0
  ) {
    // ── Start slide ──
    player.isSliding = true;
    player.slideTimer = SLIDE_DURATION;

    // Lock slide direction: normalize current horizontal velocity
    const invSpeed = 1 / hSpeed;
    player.slideDirection.x = player.velocity.x * invSpeed;
    player.slideDirection.z = player.velocity.z * invSpeed;

    if (inBhopWindow) {
      // ── Bhop chain: increment chain and boost speed ──
      player.bhopChain++;
      player.bhopMultiplier = Math.min(
        BHOP_MAX_MULTIPLIER,
        1.0 + player.bhopChain * BHOP_SPEED_BONUS,
      );
      player.bhopLandingTimer = 0;
      player.slideCooldownTimer = 0;

      // Use current speed (preserved from air) boosted by chain multiplier,
      // but at least SLIDE_SPEED
      const bhopSpeed = Math.max(hSpeed, SLIDE_SPEED) * player.bhopMultiplier;
      player.velocity.x = player.slideDirection.x * bhopSpeed;
      player.velocity.z = player.slideDirection.z * bhopSpeed;
    } else {
      // ── Normal slide: reset bhop chain ──
      player.bhopChain = 0;
      player.bhopMultiplier = 1.0;

      // Boost velocity in the slide direction
      player.velocity.x = player.slideDirection.x * SLIDE_SPEED;
      player.velocity.z = player.slideDirection.z * SLIDE_SPEED;
    }

    player.isCrouching = true; // Sliding implies crouching
  }

  // ── Bhop chain decay: if on ground, not sliding, and window expired ──
  if (onGround && !player.isSliding && player.bhopLandingTimer <= 0 && player.bhopMultiplier > 1.0) {
    player.bhopMultiplier = Math.max(1.0, player.bhopMultiplier - BHOP_DECAY_RATE * dt);
    if (player.bhopMultiplier <= 1.0) {
      player.bhopChain = 0;
      player.bhopMultiplier = 1.0;
    }
  }

  // ── Handle active slide ──
  if (player.isSliding) {
    player.slideTimer -= dt;

    // Check slide end conditions
    const slideHSpeed = Math.sqrt(
      player.velocity.x * player.velocity.x +
      player.velocity.z * player.velocity.z,
    );
    const shouldEndSlide =
      player.slideTimer <= 0 ||      // Timer expired
      !input.crouch ||                // Released crouch
      input.jump ||                   // Trying to jump
      slideHSpeed < 1.0;             // Hit wall / lost speed

    if (shouldEndSlide) {
      // ── End slide ──
      player.isSliding = false;
      player.slideTimer = 0;
      player.slideCooldownTimer = SLIDE_COOLDOWN;

      // If jumping out of a slide, mark as bhop airborne to enable chaining
      if (input.jump && onGround) {
        player.bhopFromSlide = true;
        player.velocity.y = JUMP_VELOCITY;
        // Preserve horizontal velocity (no crouch penalty)
        player.isCrouching = false;
      } else {
        // Normal slide end — transition: stay crouched if crouch is still held
        player.isCrouching = input.crouch;

        // If not jumping out, the chain is broken
        if (!input.jump) {
          player.bhopChain = 0;
          // Don't instantly reset multiplier — let it decay smoothly
        }
      }
    } else {
      // ── Slide physics: direction-locked, slide friction only ──
      player.isCrouching = true; // Stay crouched during slide

      // Apply slide friction (decelerate along locked direction)
      applySlideFriction(player, dt);

      // No direction change, no acceleration, no jump while sliding
      // Gravity still applies (below)
      // Skip to gravity + integration
    }
  }

  if (!player.isSliding) {
    // ── Normal movement (not sliding) ──

    // 1. Crouch toggle
    player.isCrouching = input.crouch;

    // 2. Compute wish direction (relative to yaw)
    const wish = getWishDirection(player, input);

    // 3. Determine max speed from weapon + crouch
    const maxSpeed = getMovementSpeed(player);

    if (onGround) {
      // ── Ground movement ──

      // Apply friction FIRST (Source engine order: friction → accelerate)
      applyFriction(player, dt);

      // Then accelerate toward wish direction
      if (wish.x !== 0 || wish.z !== 0) {
        accelerate(player, wish.x, wish.z, maxSpeed, GROUND_ACCEL, dt);
      }

      // Jump
      if (input.jump && !player.isCrouching) {
        player.velocity.y = JUMP_VELOCITY;
      }
    } else {
      // ── Air movement ──
      // No friction in air (this enables bunnyhopping / air strafing)
      if (wish.x !== 0 || wish.z !== 0) {
        accelerate(player, wish.x, wish.z, AIR_SPEED_CAP, AIR_ACCEL, dt);
      }
    }
  }

  // 5. Gravity (always applies)
  player.velocity.y += GRAVITY * dt;

  // 6. Sub-stepped integration + collision resolution.
  //    Break the movement into small steps so the player cannot tunnel through
  //    thin walls when moving at high speed (bunnyhopping, falling, etc.).
  const height = player.isSliding
    ? SLIDE_HEIGHT
    : player.isCrouching
      ? PLAYER_CROUCH_HEIGHT
      : PLAYER_HEIGHT;
  const speed = Math.sqrt(
    player.velocity.x * player.velocity.x +
    player.velocity.y * player.velocity.y +
    player.velocity.z * player.velocity.z,
  );
  const moveDistance = speed * dt;
  const steps = Math.max(1, Math.ceil(moveDistance / MAX_MOVE_STEP));
  const subDt = dt / steps;

  let px = player.position.x;
  let py = player.position.y;
  let pz = player.position.z;
  let pvx = player.velocity.x;
  let pvy = player.velocity.y;
  let pvz = player.velocity.z;
  let grounded = false;

  for (let i = 0; i < steps; i++) {
    const newX = px + pvx * subDt;
    const newY = py + pvy * subDt;
    const newZ = pz + pvz * subDt;

    const resolved = resolveCollisions(
      newX, newY, newZ,
      pvx, pvy, pvz,
      PLAYER_RADIUS,
      height,
      onGround || grounded,
    );

    px = resolved.x;
    py = resolved.y;
    pz = resolved.z;
    pvx = resolved.vx;
    pvy = resolved.vy;
    pvz = resolved.vz;
    if (resolved.grounded) grounded = true;
  }

  player.position.set(px, py, pz);
  player.velocity.set(pvx, pvy, pvz);

  // Update grounded state from collision result (overrides the pre-move check).
  // This ensures landing on boxes, tyres, or any surface counts as grounded.
  player.isGrounded = grounded;

  // ── Smooth eye height transition ──
  const targetEyeHeight = player.isSliding
    ? EYE_HEIGHT_SLIDE
    : player.isCrouching
      ? EYE_HEIGHT_CROUCH
      : EYE_HEIGHT_STAND;
  player.eyeHeightSmooth += (targetEyeHeight - player.eyeHeightSmooth) * Math.min(1, 12 * dt);
}

/**
 * Get the current maximum movement speed for the player.
 * Accounts for weapon, crouching, and tagging (hit slowdown).
 * Must match server GetEffectiveMaxSpeed() exactly.
 */
export function getMovementSpeed(player: LocalPlayer): number {
  const weaponCfg = WEAPONS[player.currentWeapon];
  const baseSpeed = weaponCfg.moveSpeed;
  let speed = baseSpeed;

  // Apply tagging
  if (player.tagSlowdown > 0) {
    speed = speed * (1.0 - player.tagSlowdown);
  }

  // Apply crouching
  if (player.isCrouching) {
    speed *= CROUCH_SPEED_MULT;
  }

  // Absolute floor: never below 34% of base speed
  const floor = baseSpeed * CROUCH_SPEED_MULT;
  if (speed < floor) {
    speed = floor;
  }

  return speed;
}

/**
 * Returns true if the player has meaningful horizontal velocity.
 */
export function isMoving(player: LocalPlayer): boolean {
  if (player.isSliding) return true;
  const hSq = player.velocity.x * player.velocity.x +
               player.velocity.z * player.velocity.z;
  return hSq > MOVING_THRESHOLD * MOVING_THRESHOLD;
}

/**
 * Returns the smoothly interpolated eye height for the local player.
 * Use this instead of a fixed EYE_HEIGHT for crouch/slide camera transitions.
 */
export function getSmoothedEyeHeight(player: LocalPlayer): number {
  return player.eyeHeightSmooth;
}

/**
 * Apply a tagging (hit slowdown) effect to the local player.
 * Called when a damage event is received from the server.
 * Must match server ApplyTag() exactly.
 */
export function applyTag(player: LocalPlayer, weaponType: string, hasArmor: boolean): void {
  const slowAmount = TAG_SLOW_AMOUNTS[weaponType] ?? 0;
  if (slowAmount === 0) return;

  const adjusted = hasArmor ? slowAmount * 0.5 : slowAmount;
  player.tagSlowdown = adjusted;
  player.tagTimer = TAG_DURATION;
}

// ── Internal: Quake/Source movement functions ──

/**
 * Apply ground friction to horizontal velocity.
 * Source engine: friction reduces speed each tick, with a stopspeed floor
 * that prevents infinite sliding.
 */
function applyFriction(player: LocalPlayer, dt: number): void {
  const vx = player.velocity.x;
  const vz = player.velocity.z;
  const speed = Math.sqrt(vx * vx + vz * vz);

  if (speed < 0.01) {
    player.velocity.x = 0;
    player.velocity.z = 0;
    return;
  }

  // Use stopspeed as minimum for friction calculation
  // This is the key to preventing "ice skating" in CS
  const control = Math.max(speed, STOP_SPEED);
  const drop = control * FRICTION * dt;

  let newSpeed = Math.max(speed - drop, 0);
  newSpeed /= speed; // ratio to scale velocity

  player.velocity.x *= newSpeed;
  player.velocity.z *= newSpeed;
}

/**
 * Apply friction during a slide.
 * Uses SLIDE_FRICTION instead of normal ground friction.
 * Velocity is kept aligned to the locked slide direction.
 */
function applySlideFriction(player: LocalPlayer, dt: number): void {
  const vx = player.velocity.x;
  const vz = player.velocity.z;
  const speed = Math.sqrt(vx * vx + vz * vz);

  if (speed < 0.01) {
    player.velocity.x = 0;
    player.velocity.z = 0;
    return;
  }

  const drop = speed * SLIDE_FRICTION * dt;
  let newSpeed = Math.max(speed - drop, 0);

  // Re-project onto the locked slide direction (prevents drift)
  player.velocity.x = player.slideDirection.x * newSpeed;
  player.velocity.z = player.slideDirection.z * newSpeed;
}

/**
 * Quake/Source-style acceleration.
 * Only adds speed in the wish direction up to the max speed.
 * The projection trick (dot product with wish) is what enables air strafing.
 */
function accelerate(
  player: LocalPlayer,
  wishX: number, wishZ: number,
  wishSpeed: number,
  accel: number,
  dt: number,
): void {
  // Current speed projected onto the wish direction
  const currentSpeed = player.velocity.x * wishX + player.velocity.z * wishZ;

  // How much speed we need to add to reach wishSpeed in this direction
  const addSpeed = wishSpeed - currentSpeed;
  if (addSpeed <= 0) return;

  // Acceleration amount this tick
  let accelSpeed = accel * wishSpeed * dt;

  // Don't exceed the needed speed
  if (accelSpeed > addSpeed) {
    accelSpeed = addSpeed;
  }

  // Apply
  player.velocity.x += accelSpeed * wishX;
  player.velocity.z += accelSpeed * wishZ;
}

/**
 * Compute the normalized wish direction from WASD input, relative to yaw.
 */
function getWishDirection(
  player: LocalPlayer,
  input: InputState,
): { x: number; z: number } {
  let fx = 0;
  let fz = 0;

  // Forward is -Z in three.js convention (matches server)
  if (input.forward) fz -= 1;
  if (input.back)    fz += 1;
  if (input.left)    fx -= 1;
  if (input.right)   fx += 1;

  if (fx === 0 && fz === 0) return { x: 0, z: 0 };

  // Normalize diagonal movement
  const len = Math.sqrt(fx * fx + fz * fz);
  fx /= len;
  fz /= len;

  // Rotate by yaw to match camera direction
  // Camera forward at yaw=θ is (-sinθ, -cosθ) in XZ, right is (cosθ, -sinθ)
  const sinYaw = Math.sin(player.yaw);
  const cosYaw = Math.cos(player.yaw);

  return {
    x: fx * cosYaw + fz * sinYaw,
    z: -fx * sinYaw + fz * cosYaw,
  };
}
